import { useEffect, useState } from 'react';

/**
 * The browser's position, rounded to roughly a kilometre, or null.
 *
 * Coarse deliberately, for the same reasons as the mobile hook: ranking only
 * needs to know which venues are nearer, a rounded origin keeps stored travel
 * times shared across a neighbourhood instead of one lookup per person, and an
 * approximate area is a much smaller promise to keep than a position.
 *
 * `enableHighAccuracy` stays off — it wakes GPS on a phone for precision that
 * is about to be rounded away.
 *
 * @returns {{coords: [number, number] | null, status: 'idle'|'granted'|'denied'}}
 */
export function useCoarseLocation() {
  const [coords, setCoords] = useState(null);
  const [status, setStatus] = useState('idle');

  useEffect(() => {
    if (!navigator.geolocation) {
      setStatus('denied');
      return undefined;
    }

    let active = true;

    navigator.geolocation.getCurrentPosition(
      (position) => {
        if (!active) return;
        setCoords([
          Number(position.coords.latitude.toFixed(2)),
          Number(position.coords.longitude.toFixed(2)),
        ]);
        setStatus('granted');
      },
      () => {
        // Denied, unavailable, or timed out. The deck works without it.
        if (active) setStatus('denied');
      },
      { enableHighAccuracy: false, timeout: 8000, maximumAge: 10 * 60 * 1000 }
    );

    return () => {
      active = false;
    };
  }, []);

  return { coords, status };
}
